import {
  Calendar,
  MoreVertical,
  Reply,
  Send,
  User,
} from "@tamagui/lucide-icons";
import { observer } from "mobx-react-lite";
import React, { useMemo } from "react";
import { useTranslation } from "react-i18next";
import type { GestureResponderEvent } from "react-native";
import { View, XStack, YStack } from "tamagui";
import { useApi } from "../../core/apis";
import { getDayJs } from "../../core/dayJs";
import { Permission } from "../../permissions/types";
import { Body } from "../../ui/Body";
import { Group } from "../../ui/Group";
import { InfoRow, type InfoRowItemProps } from "../../ui/InfoRow";
import { Subtext } from "../../ui/Subtext";
import { AvatarSize, UserPropsAvatar } from "../../ui/UserAvatar";
import { FeedItemManageSheet } from "../feed/ui/FeedItemManageSheet";
import { FeedItemReactionShareSheet } from "../feed/ui/FeedItemReactionsShareSheet";
import { FeedTextInput } from "../feed/ui/FeedTextInput";
import { FeedTextWithMentions } from "../feed/ui/FeedTextWithMentions";
import { useDisplayNameInfo } from "../feed/utils";
import { FeedItemCommentReplyUI } from "../feedcommentreply/FeedItemCommentReplyUI";
import type { BusinessFeedCommentModel } from "./BusinessFeedCommentModel";

export const FeedItemCommentUI = observer(
  ({ comment }: { comment: BusinessFeedCommentModel }) => {
    const { businessFeedCommentsReplyApi } = useApi();
    const { t } = useTranslation();
    const author = comment.author;

    const [showReactionSheet, setShowReactionSheet] = React.useState(false);
    const [showManageSheet, setShowManageSheet] = React.useState(false);
    const [showReplies, setShowReplies] = React.useState(false);
    const [isEditMode, setIsEditMode] = React.useState(false);
    const getDisplayInfo = useDisplayNameInfo();

    const toggleReplies = React.useCallback(
      (e: GestureResponderEvent) => {
        e.stopPropagation();
        setShowReplies(!showReplies);
      },
      [showReplies],
    );

    const infoRowItems = useMemo<InfoRowItemProps[]>(() => {
      const displayInfo = getDisplayInfo(author);

      const items: InfoRowItemProps[] = [
        {
          icon: User,
          text:
            (displayInfo?.text ? `(${displayInfo?.text}) ` : "") +
            (author?.displayName ?? ""),
        },
        {
          icon: Calendar,
          text: getDayJs(comment.data.ts),
        },
        {
          icon: Reply,
          text: (
            <Subtext onPress={toggleReplies} textDecorationLine={"underline"}>
              {comment.data.repliesCount > 0
                ? `${comment.data.repliesCount} ${t("business:feed_replies")}`
                : t("business:feed_reply")}
            </Subtext>
          ),
        },
      ];

      comment.reactionRowItemCounts.forEach((reaction, _index) => {
        const Icon = reaction.icon;

        items.push({ text: `${reaction.count}`, icon: Icon });
      });

      return items;
    }, [getDisplayInfo, t, toggleReplies, author, comment]);

    const replies = comment.replies;

    if (!author) {
      return null;
    }

    if (comment.data.deleted) {
      return (
        <Body muted fontStyle={"italic"}>
          {t("business:feed_content_deleted")}
        </Body>
      );
    }

    return (
      <View width={"100%"}>
        <XStack
          width={"100%"}
          gap={"$medium"}
          onPress={(e: GestureResponderEvent) => {
            if (isEditMode) {
              return;
            }
            e.stopPropagation();
            setShowReactionSheet(true);
          }}
          cursor={"pointer"}
        >
          <UserPropsAvatar
            {...author}
            size={AvatarSize.Smaller}
            showBorder={comment.feedItem.businessPlan.checkPermission(
              Permission.AvatarShowExtraBorder,
              author.userUid,
            )}
          />

          <YStack gap="$small" flex={1}>
            {comment.data.hidden && (
              <Body muted fontStyle={"italic"}>
                {t("business:feed_content_hidden")}
              </Body>
            )}

            {comment.data.edited && !comment.data.hidden && (
              <Body muted fontStyle={"italic"}>
                {t("business:feed_content_edited")}
              </Body>
            )}

            {!!comment.data.reported &&
              !comment.data.hidden &&
              comment.isAdmin && (
                <Body muted fontStyle={"italic"}>
                  {t("business:feed_content_reported")}
                </Body>
              )}

            {!comment.data.hidden && !isEditMode && (
              <FeedTextWithMentions
                text={comment.data.text}
                mentions={comment.data.mentions}
                businessPlan={comment.feedItem.businessPlan}
              />
            )}

            {isEditMode && (
              <FeedTextInput
                autoFocus
                businessPlan={comment.feedItem.businessPlan}
                placeholder={t("business:feed_edit_comment")}
                placeholderFocused={t("business:feed_mention_tip")}
                defaultValue={comment.data.text}
                onSubmitEditing={async (e) => {
                  await comment.edit(e.nativeEvent.text);
                  setIsEditMode(false);
                }}
              />
            )}

            <InfoRow items={infoRowItems} />
          </YStack>

          <View
            padding={"$small"}
            onPress={(e: GestureResponderEvent) => {
              e.stopPropagation();
              setShowManageSheet(!showManageSheet);
            }}
            cursor={"pointer"}
            marginRight={-12}
          >
            <MoreVertical color={"$muted"} />
          </View>
        </XStack>

        {showReplies && (
          <YStack
            marginLeft={45}
            borderTopWidth={1}
            borderTopColor={"$divider"}
            marginTop={"$medium"}
            paddingTop="$medium"
            gap={"$medium"}
          >
            {comment.hasMoreReplies && (
              <Body
                width="100%"
                textAlign="center"
                muted
                textDecorationLine="underline"
                cursor="pointer"
                onPress={() => {
                  void businessFeedCommentsReplyApi.getFeedCommentReplyForCommentItem(
                    comment.uid,
                    replies[0].data.ts,
                  );
                }}
              >
                {t("business:feed_load_replies")}
              </Body>
            )}

            <Group borderTop={false}>
              {replies.map((reply) => (
                <FeedItemCommentReplyUI key={reply.data.uid} reply={reply} />
              ))}
            </Group>

            <FeedTextInput
              businessPlan={comment.feedItem.businessPlan}
              type={"border"}
              submitOnEnter
              icon={Send}
              placeholder={t("business:feed_reply_now")}
              onSubmitEditing={(e) => {
                comment.addReplyComment({
                  text: e.nativeEvent.text,
                });
              }}
            />
          </YStack>
        )}

        <FeedItemReactionShareSheet
          sheetId={"feed_comment_reactions"}
          commentItemUid={comment.uid}
          open={showReactionSheet}
          onOpenChange={setShowReactionSheet}
        />

        <FeedItemManageSheet
          sheetId={"feed_comment_manage"}
          editOption={{
            show: comment.isAuthor,
            callback: () => setIsEditMode(!isEditMode),
          }}
          hideOption={{
            show: comment.isAdmin,
            isHidden: comment.data.hidden,
            callback: () => comment.saveEditAction("hidden"),
          }}
          deleteOption={{
            show: comment.isAdmin || comment.isAuthor,
            callback: () => comment.saveEditAction("deleted"),
          }}
          reportOption={{
            show: !comment.isAuthor,
            callback: () => comment.saveEditAction("reported"),
          }}
          open={showManageSheet}
          onOpenChange={setShowManageSheet}
        />
      </View>
    );
  },
);
